import { GameDataState } from './AppCasino/game-modal/GameData.state';
import { CasinoSidebarGameInfoState } from './AppCasino/CasinoSidebarGameInfo.state';
import { LiveCasinoSocket } from './AppCasino/casino-sockets/real-time-lobby-pragmatic-socket/LiveCasinoSocket';
import { CasinoTableModel } from './AppCasino/casino-sockets/real-time-lobby-pragmatic-socket/CasinoTableModel';
import { SocketGameList } from './AppCasino/casino-sockets/real-time-lobby-pragmatic-socket/SocketGameList';
import { SocketGameDetails } from './AppCasino/casino-sockets/real-time-lobby-pragmatic-socket/SocketGameDetails';
import { GameStoreExt } from 'src/domains/casino/state/AppCasino/store/game-store/GameStoreExt';
import { CasinoGameModelExt } from './AppCasino/store/game-store/CasinoGameModelExt';
import { CasinoCollectionModelExt } from './AppCasino/store/game-store/CasinoCollectionModelExt';
import { GameSharedType, FilterCategoryType } from './AppCasino/store/game-store/typesExt';
import { HomeCollections } from './AppCasino/HomeCollections.state';
import { CasinoNavigationState } from './AppCasino/CasinoNavigation.state';
import { RedirectState } from 'src/domains/casino/state/AppCasino/Redirect.state';
import { BannerStore } from 'src/domains/casino/state/AppCasino/store/banner-store/Banner.store';
import { GameModalState } from './AppCasino/game-modal/GameModal.state';
import { MiniGamesListState } from 'src/domains/casino/state/AppCasino/MiniGamesList.state';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { BannerType } from 'src/domains/casino/state/AppCasino/store/banner-store/types';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { CasinoPageState } from 'src/domains/casino/state/AppCasino/CasinoPage.state';
import { CasinoSearchExt } from './AppCasino/store/game-store/CasinoSearchExt';
import { getAccountRoute, getCasinoRoute } from 'src/domains/casino/utils/utils';
import { RealTimeLobbySocket } from './AppCasino/casino-sockets/real-time-lobby-socket/RealTimeLobbySocket';
import { ConfigState } from './AppCasino/ConfigState';
import { GameDetailsCommonModel } from 'src/domains/casino/state/AppCasino/casino-sockets/real-time-lobby-socket/GameDetailsCommonModel';
import { Common } from 'src/domains/common/Common';
import { CollectionType } from 'src/domains/casino/types/types';
import { observable, when } from 'mobx';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';

type GameWrapperType = 'slider' | 'slider-grid' | 'square' | 'rectangle' | 'numeric-trending';

export {
    CasinoTableModel,
    SocketGameList,
    SocketGameDetails,
    CasinoGameModelExt,
    CasinoCollectionModelExt,
    GameSharedType,
    FilterCategoryType,
    CasinoPageState,
    CasinoNavigationState,
    GameWrapperType,
    GameDataState,
    BannerType,
    GameDetailsCommonModel,
    CollectionType,
};

export class AppCasinoState {
    @observable public gameStoreExt: GameStoreExt;
    public readonly gameModalState: GameModalState;
    public readonly bannerStore: BannerStore;
    public readonly casinoSidebarGameInfoState: CasinoSidebarGameInfoState;
    public readonly liveCasinoSocket: LiveCasinoSocket;
    public readonly realTimeLobbySocket: RealTimeLobbySocket;
    public readonly redirectState: RedirectState;
    public readonly homeCollections: HomeCollections;
    public readonly miniGamesListState: MiniGamesListState;
    public readonly casinoPageState: AutoMap<CollectionType, AutoMap<string | null, CasinoPageState>>;

    public static get = AutoWeakMap.create((common: Common) => new AppCasinoState(common));

    private constructor(private readonly common: Common) {
        this.bannerStore = new BannerStore(this.common.session);
        this.redirectState = new RedirectState(this.common);
        const search = new CasinoSearchExt();
        this.gameModalState = new GameModalState(this.common, this.redirectState);
        this.gameStoreExt = new GameStoreExt(search, this.common, this.redirectState, this.gameModalState);

        StarRouter.get(this.common).onChangeCurrentView((prevCurrentView, nextCurrentView) => {
            const prevCasinoRoute = getCasinoRoute(prevCurrentView);
            const nextCasinoRoute = getCasinoRoute(nextCurrentView);
            const { hideAllCasinoGamesOnChristmas, casinoPublishedCollections } = new ConfigState(this.common)
                .commonConfig;
            const env = EnvironmentState.get(this.common);

            // redirect unpublished collection page to homepage
            if (
                (nextCurrentView?.name === 'casino' && !(nextCurrentView.game === 'bingo')) ||
                nextCurrentView?.name === 'live-casino' ||
                nextCurrentView?.name === 'virtuals'
            ) {
                if (env.isBrowser && casinoPublishedCollections.includes(nextCurrentView.name) === false) {
                    const homepageUrl = `${location.protocol}//${location.host}`;
                    location.replace(homepageUrl);
                }
            }

            // reset search on route change
            if (prevCasinoRoute !== nextCasinoRoute) {
                search.setSearch('');
                search.setIsSearchOpen(false);
                search.setIsDropdownOpen(false);
            }

            // redirect casino to christmas page
            if (env.isBrowser && hideAllCasinoGamesOnChristmas) {
                if (
                    nextCasinoRoute === 'casino' ||
                    nextCasinoRoute === 'live-casino' ||
                    nextCasinoRoute === 'virtuals'
                ) {
                    location.replace('https://christmas.starsports.bet/');
                }
            }

            if (
                (nextCurrentView?.name === 'casino' ||
                    nextCurrentView?.name === 'live-casino' ||
                    nextCurrentView?.name === 'virtuals') &&
                nextCurrentView.game !== null &&
                nextCurrentView.game !== undefined
            ) {
                // launch bingo from url
                if (nextCurrentView.game === 'bingo') {
                    if (casinoPublishedCollections.includes('bingo') === true) {
                        this.gameModalState.startBingoGame();
                    } else if (env.isBrowser && casinoPublishedCollections.includes('bingo') === false) {
                        const homepageUrl = `${location.protocol}//${location.host}`;
                        location.replace(homepageUrl);
                    }
                } else {
                    // launch model game from url
                    const gameId = nextCurrentView.game;

                    when(
                        () =>
                            this.gameStoreExt.allCollections
                                .map((collection) => collection.games)
                                .flat()
                                .some((game) => game.id === gameId),
                        () => {
                            const gameModel = this.gameStoreExt.getGameModel(gameId);

                            if (gameModel === null) {
                                console.error(`Could not find game model for game id: ${gameId}`);
                                return;
                            }

                            this.gameModalState.startGame({
                                type: 'model',
                                gameModel,
                                mode: nextCurrentView.mode ?? undefined,
                            });
                        }
                    );
                }
            }
        });

        StarRouter.get(this.common).onChangeAccountView((prevAccountView, nextAccountView) => {
            const prevAccountRoute = getAccountRoute(prevAccountView);
            const nextAccountRoute = getAccountRoute(nextAccountView);

            if (prevAccountRoute !== nextAccountRoute) {
                if (prevAccountRoute !== 'deposit' && nextAccountRoute === 'deposit') {
                    this.gameModalState.onDepositOpen();
                }
                if (prevAccountRoute === 'deposit' && nextAccountRoute !== 'deposit') {
                    this.gameModalState.onDepositClose();
                }

                if (
                    prevAccountRoute === 'login' &&
                    nextAccountRoute !== 'login' &&
                    this.common.session.isAuthorized === false
                ) {
                    setTimeout(() => {
                        // TODO - setTimeout to be removed after router refactor (now it's needed to prevent infinite loop)
                        this.gameModalState.closeGame();
                    });
                }
            }
        });
        this.homeCollections = new HomeCollections(this.gameStoreExt, this.redirectState);

        this.miniGamesListState = new MiniGamesListState(this.gameStoreExt, this.common);

        this.casinoSidebarGameInfoState = new CasinoSidebarGameInfoState(this.redirectState);

        this.liveCasinoSocket = new LiveCasinoSocket(this.common);

        this.realTimeLobbySocket = new RealTimeLobbySocket(this.common);

        this.casinoPageState = new AutoMap(
            (collection: CollectionType) =>
                new AutoMap(
                    (tabId: string | null) =>
                        new CasinoPageState(
                            this.redirectState,
                            this.common,
                            this.gameStoreExt.getCollection(collection),
                            collection,
                            tabId,
                            search
                        )
                )
        );
    }
}
